import { WarningFilled } from "@ant-design/icons";
import { Tooltip } from "antd";

import type { TClientForm } from "../../types/index.type";

/**
 * Warns that a patient's MRN does not identify them in the EHR.
 *
 * eCW keys Patient Lookup on the account number but keys every patient-scoped API on
 * an internal id, and the two number spaces overlap — so a mistyped MRN is usually
 * still a *real* MRN, just somebody else's. It does not error; it silently returns
 * another patient's chart. When verification says the MRN is wrong, transfer refuses,
 * and the clinician needs to know why before they record against this patient.
 *
 * Renders nothing when verification passed or was never run.
 */

const MESSAGES: Record<string, string> = {
  not_found: "This MRN doesn't exist in the EHR. Note transfer will not work until it's corrected.",
  ambiguous: "This MRN matches more than one chart in the EHR, so we can't tell which patient it is.",
  mismatch: "This MRN is registered to a different patient in the EHR. Please correct it before transferring notes.",
};

type Props = { verification?: TClientForm["ehr_verification"] };

export const EhrVerificationBadge = ({ verification }: Props) => {
  if (!verification || verification === "verified") return null;

  const message = MESSAGES[verification] ?? "This patient's MRN could not be verified against the EHR.";

  return (
    <Tooltip placement="top" title={message}>
      <WarningFilled
        aria-label="MRN not verified against the EHR"
        // Matches the failure red TransferBanner uses — this is the same class of
        // problem surfaced earlier, before a note is recorded rather than after.
        style={{ color: "#ff4d4f", flexShrink: 0 }}
      />
    </Tooltip>
  );
};

export default EhrVerificationBadge;
